import type { BookLibrary } from "../models/types";
import { createEmptyLibrary } from "../models/library";

/**
 * Microsoft Graph / OneDrive configuration.
 *
 * To use OneDrive storage you must register an Azure AD app:
 *   1. Go to https://portal.azure.com -> Azure Active Directory -> App registrations
 *   2. New registration -> Single-page application
 *   3. Set redirect URI to your hosting URL (e.g. http://localhost:5173/ for dev)
 *   4. Under API permissions, add Microsoft Graph -> Files.ReadWrite
 *   5. Copy the Application (client) ID into the constant below
 */
const CLIENT_ID = "1cfc9e7e-5c12-47a5-a2c2-d3c9fcd5313e";
const AUTHORITY = "https://login.microsoftonline.com/common";
const SCOPES = ["Files.ReadWrite", "offline_access"];

/** The path in OneDrive where we store the JSON library file */
const ONEDRIVE_FILE_PATH = "/Apps/BookList/library.json";

/** Graph API base */
const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

// ---------------------------------------------------------------------------
// Session-storage keys for persisting PKCE state across the redirect
// ---------------------------------------------------------------------------
const SS_CODE_VERIFIER = "booklist_pkce_verifier";
const SS_AUTH_STATE = "booklist_auth_state";

// ---------------------------------------------------------------------------
// LocalStorage keys for persisting tokens across sessions
// ---------------------------------------------------------------------------
const LS_ACCESS_TOKEN = "booklist_access_token";
const LS_TOKEN_EXPIRY = "booklist_token_expiry";
const LS_REFRESH_TOKEN = "booklist_refresh_token";

// ---------------------------------------------------------------------------
// Token management - Authorization Code flow with PKCE (redirect-based)
// ---------------------------------------------------------------------------

let _accessToken: string | null = null;
let _tokenExpiry = 0;
let _refreshToken: string | null = null;

// Restore tokens from localStorage on module load
(function restoreTokens() {
  const savedToken = localStorage.getItem(LS_ACCESS_TOKEN);
  const savedExpiry = localStorage.getItem(LS_TOKEN_EXPIRY);
  const savedRefresh = localStorage.getItem(LS_REFRESH_TOKEN);
  if (savedToken && savedExpiry) {
    _accessToken = savedToken;
    _tokenExpiry = Number(savedExpiry);
  }
  if (savedRefresh) {
    _refreshToken = savedRefresh;
  }
})();

export function isConfigured(): boolean {
  return CLIENT_ID.length > 0;
}

export function isSignedIn(): boolean {
  return _accessToken !== null && Date.now() < _tokenExpiry;
}

/**
 * Returns true if we have a refresh token that could be used to silently
 * obtain a new access token (even if the current access token is expired).
 */
export function hasRefreshToken(): boolean {
  return _refreshToken !== null;
}

/** Persist current tokens to localStorage */
function persistTokens(): void {
  if (_accessToken) {
    localStorage.setItem(LS_ACCESS_TOKEN, _accessToken);
    localStorage.setItem(LS_TOKEN_EXPIRY, String(_tokenExpiry));
  }
  if (_refreshToken) {
    localStorage.setItem(LS_REFRESH_TOKEN, _refreshToken);
  }
}

/** Clear all persisted token data */
function clearPersistedTokens(): void {
  localStorage.removeItem(LS_ACCESS_TOKEN);
  localStorage.removeItem(LS_TOKEN_EXPIRY);
  localStorage.removeItem(LS_REFRESH_TOKEN);
}

// ---------------------------------------------------------------------------
// PKCE helpers
// ---------------------------------------------------------------------------

function generateCodeVerifier(): string {
  const array = new Uint8Array(48);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function deriveCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

function base64UrlEncode(buffer: Uint8Array): string {
  let str = "";
  for (const byte of buffer) str += String.fromCharCode(byte);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ---------------------------------------------------------------------------
// Redirect-based sign-in
// ---------------------------------------------------------------------------

/**
 * Redirect the user to the Microsoft login page.
 * PKCE verifier + state are saved to sessionStorage so we can finish
 * the flow when the user is redirected back.
 */
export async function signIn(): Promise<void> {
  if (!isConfigured()) {
    throw new Error(
      "OneDrive integration is not configured. Set CLIENT_ID in src/services/onedrive.ts",
    );
  }

  const redirectUri = getRedirectUri();
  const state = crypto.randomUUID();
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await deriveCodeChallenge(codeVerifier);

  // Persist across the redirect
  sessionStorage.setItem(SS_CODE_VERIFIER, codeVerifier);
  sessionStorage.setItem(SS_AUTH_STATE, state);

  const authUrl = new URL(`${AUTHORITY}/oauth2/v2.0/authorize`);
  authUrl.searchParams.set("client_id", CLIENT_ID);
  authUrl.searchParams.set("response_type", "code");
  authUrl.searchParams.set("redirect_uri", redirectUri);
  authUrl.searchParams.set("scope", SCOPES.join(" "));
  authUrl.searchParams.set("state", state);
  authUrl.searchParams.set("response_mode", "query");
  authUrl.searchParams.set("code_challenge", codeChallenge);
  authUrl.searchParams.set("code_challenge_method", "S256");

  // Full-page redirect - works on mobile, no popup blockers
  window.location.href = authUrl.toString();
}

/**
 * Call this on every page load BEFORE rendering the app.
 * If the URL contains ?code=...&state=..., we complete the token exchange,
 * clean up the URL, and the app will see isSignedIn() === true.
 *
 * Returns true if a redirect was handled (so the caller can reload data).
 */
export async function handleRedirectIfNeeded(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const error = params.get("error");

  if (error) {
    const desc = params.get("error_description") || error;
    cleanUrl();
    throw new Error(`Authentication error: ${desc}`);
  }

  if (!code || !state) return false;

  const savedState = sessionStorage.getItem(SS_AUTH_STATE);
  const codeVerifier = sessionStorage.getItem(SS_CODE_VERIFIER);

  // Clean up sessionStorage regardless
  sessionStorage.removeItem(SS_AUTH_STATE);
  sessionStorage.removeItem(SS_CODE_VERIFIER);

  if (state !== savedState || !codeVerifier) {
    cleanUrl();
    throw new Error("Invalid auth state - please sign in again.");
  }

  // Exchange authorization code for token
  const redirectUri = getRedirectUri();
  const tokenUrl = `${AUTHORITY}/oauth2/v2.0/token`;
  const body = new URLSearchParams({
    client_id: CLIENT_ID,
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
    scope: SCOPES.join(" "),
  });

  const resp = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!resp.ok) {
    const err = await resp.text();
    cleanUrl();
    throw new Error(`Token exchange failed: ${err}`);
  }

  const token = await resp.json();
  _accessToken = token.access_token;
  _tokenExpiry = Date.now() + (Number(token.expires_in) - 60) * 1000;
  if (token.refresh_token) {
    _refreshToken = token.refresh_token;
  }
  persistTokens();

  // Remove code/state from address bar so a refresh doesn't replay
  cleanUrl();

  return true;
}

export function signOut(): void {
  _accessToken = null;
  _tokenExpiry = 0;
  _refreshToken = null;
  clearPersistedTokens();
}

/**
 * Attempt to silently refresh the access token using a stored refresh token.
 * Returns true if the token was refreshed successfully, false otherwise.
 * This should be called on page load when the access token is expired but
 * a refresh token is available.
 */
export async function trySilentRefresh(): Promise<boolean> {
  if (!_refreshToken) return false;

  try {
    const redirectUri = getRedirectUri();
    const tokenUrl = `${AUTHORITY}/oauth2/v2.0/token`;
    const body = new URLSearchParams({
      client_id: CLIENT_ID,
      grant_type: "refresh_token",
      refresh_token: _refreshToken,
      redirect_uri: redirectUri,
      scope: SCOPES.join(" "),
    });

    const resp = await fetch(tokenUrl, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });

    if (!resp.ok) {
      // Only clear tokens if the refresh token is definitively invalid.
      // Transient server errors (5xx) should preserve the refresh token
      // so the next app load can retry.
      if (resp.status >= 400 && resp.status < 500) {
        signOut();
      }
      return false;
    }

    const token = await resp.json();
    _accessToken = token.access_token;
    _tokenExpiry = Date.now() + (Number(token.expires_in) - 60) * 1000;
    if (token.refresh_token) {
      _refreshToken = token.refresh_token;
    }
    persistTokens();
    return true;
  } catch {
    // Network errors are transient – preserve the refresh token so the
    // next app load can retry without forcing a full re-authentication.
    return false;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Canonical redirect URI — always root to match Azure registration regardless of deep links */
function getRedirectUri(): string {
  return window.location.origin + "/";
}

/** Remove OAuth query params from the address bar without a reload */
function cleanUrl(): void {
  const url = new URL(window.location.href);
  for (const key of ["code", "state", "error", "error_description"]) {
    url.searchParams.delete(key);
  }
  window.history.replaceState({}, document.title, url.toString());
}

// ---------------------------------------------------------------------------
// Graph API helpers
// ---------------------------------------------------------------------------

async function graphFetch(path: string, init?: RequestInit): Promise<Response> {
  if (!_accessToken) throw new Error("Not signed in");

  let resp = await fetch(`${GRAPH_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${_accessToken}`,
      "Content-Type": "application/json",
      ...(init?.headers as Record<string, string>),
    },
  });

  // If we get a 401, try refreshing the token and retry once
  if (resp.status === 401 && _refreshToken) {
    const refreshed = await trySilentRefresh();
    if (refreshed) {
      resp = await fetch(`${GRAPH_BASE}${path}`, {
        ...init,
        headers: {
          Authorization: `Bearer ${_accessToken}`,
          "Content-Type": "application/json",
          ...(init?.headers as Record<string, string>),
        },
      });
    }
  }

  return resp;
}

/** Load library JSON from OneDrive, returns null if file does not exist */
export async function loadLibrary(): Promise<BookLibrary> {
  const resp = await graphFetch(
    `/me/drive/root:${ONEDRIVE_FILE_PATH}:/content`,
  );
  if (resp.status === 404) {
    const lib = createEmptyLibrary();
    await saveLibrary(lib);
    return lib;
  }
  if (!resp.ok) throw new Error(`Failed to load library: ${resp.statusText}`);
  return (await resp.json()) as BookLibrary;
}

/** Save library JSON to OneDrive */
export async function saveLibrary(library: BookLibrary): Promise<void> {
  library.lastModified = new Date().toISOString();
  const body = JSON.stringify(library, null, 2);
  const resp = await graphFetch(
    `/me/drive/root:${ONEDRIVE_FILE_PATH}:/content`,
    {
      method: "PUT",
      body,
      headers: { "Content-Type": "application/json" },
    },
  );
  if (!resp.ok) throw new Error(`Failed to save library: ${resp.statusText}`);
}
